import React, { useState } from 'react';
import {
  Button, Table, Switch, InputNumber, Input, Modal, Alert,
  Form, Select, message, Badge, Typography, Space, Tag, Popconfirm, Radio, Drawer
} from 'antd';
import {
  EditOutlined, PlusOutlined, InfoCircleOutlined, ExperimentOutlined, SettingOutlined,
} from '@ant-design/icons';
import { MOCK_AD_PLATFORMS, getEnabledPrimaryPlatforms } from '../../../utils/adPlatforms';
import { PAYMENT_CHANNELS, countConfiguredFees } from '../../../utils/paymentChannels';
import {
  MOCK_SERVICE_TYPES, REGION_LABEL, formatDiscount,
} from './serviceTypes';
import ServiceTypePolicyTable from './components/ServiceTypePolicyTable';
import PaymentFeeModal from './components/PaymentFeeModal';

const { Text } = Typography;

// ════════════════════════════════════════
//  Mock 数据
// ════════════════════════════════════════

// 三块场景各自维护底价策略：default 为国内/海外默认，services 为按服务类型的覆盖（含国内/海外）
// TODO: 联调时替换
const MOCK_GIFT_CONFIG = {
  default: {
    cn: { isDeductBasePrice: true, discountRatio: 80.00, remark: '年度战略合作协议约定八折' },
    os: { isDeductBasePrice: true, discountRatio: 100.00, remark: '' },
  },
  services: {
    '300010': {
      cn: { isDeductBasePrice: true, discountRatio: 100.00 },
      os: { isDeductBasePrice: false, discountRatio: 100.00 },
    },
  },
  updatedAt: '2026-06-30 14:30', operator: 'admin',
};

const MOCK_SELF_CONFIG = {
  default: {
    cn: { isDeductBasePrice: true, discountRatio: 80.00, remark: '年度战略合作协议约定八折' },
    os: { isDeductBasePrice: false, discountRatio: 100.00, remark: '海外暂不扣底价' },
  },
  services: {
    '300033': {
      cn: { isDeductBasePrice: true, discountRatio: 90.00 },
      os: { isDeductBasePrice: true, discountRatio: 100.00 },
    },
  },
  updatedAt: '2026-06-30 14:30', operator: 'admin',
};

// 抽佣与手续费（场景级；国内 / 海外各自独立配置是否抽佣、比例、抽佣时是否扣底价）
const MOCK_SELF_COMMISSION = {
  cn: { isCommission: true, commissionRate: 10.00, isCommissionDeductBasePrice: true },
  os: { isCommission: false, commissionRate: 10.00, isCommissionDeductBasePrice: true },
  isDeductPaymentFee: true,
  paymentFees: {
    wechat: { rate: 0.60, fixedFeeCny: 0.30 },
    alipay: { rate: 1.50, fixedFeeCny: 0.90 },
  },
  updatedAt: '2026-06-30 14:30', operator: 'admin',
};

const MOCK_PLATFORM_COMMISSION = {
  cn: { isCommission: true, commissionRate: 12.00, isCommissionDeductBasePrice: true, remark: '平台代收款按抽佣出账' },
  os: { isCommission: true, commissionRate: 8.00, isCommissionDeductBasePrice: false, remark: '' },
  isDeductPaymentFee: false,
  paymentFees: {},
  updatedAt: '', operator: '',
};

const MOCK_LICENSE_DISCOUNT = 100; // TODO: 联调时替换

// 设备流量商务（只读展示；null = 未关联，未关联不扣费）
const MOCK_TRAFFIC_GROUP = {
  name: '标准价',
  cnyPrice: 0.80,
  usdPrice: 0.12,
  exchangeRate: 7.20,
  countryPrices: [{ country: '美国', usdPrice: 0.10 }, { country: '日本', usdPrice: 0.08 }],
}; // TODO: 联调时替换（null = 未关联）

// 私有化部署商务（月租费成本比 + 弹性云资源用量成本比，均为倍数；null = 未配置）
const MOCK_PRIVATE_DEPLOY_BIZ = {
  monthlyRentRatio: null,
  elasticUsageRatio: null,
  updatedAt: '',
  operator: '',
};

// ══════════════════════════════════════════════
//  App 广告抽佣（企业详情-商务信息 Tab）
//  按 AppID 配置：广告源（企业/平台）+ 广告平台（一级，可多个）+ 抽佣比例
//  默认每个 AppID 为空配置；比例留空=不抽佣
// ══════════════════════════════════════════════

const AD_SOURCE_LABEL = { 1: '企业广告源', 2: '平台广告源' };
const AD_SOURCE_COLOR = { 1: 'orange', 2: 'red' };
const platformStatusMap = MOCK_AD_PLATFORMS.reduce((m, p) => { m[p.code] = p.status; return m; }, {});
const ENABLED_PLATFORM_OPTIONS = getEnabledPrimaryPlatforms();

// TODO: 联调时替换为接口数据（演示数据；实际默认每个 AppID 为空配置）
const MOCK_AD_APPS = [
  {
    appId: 'HS_iOS_CN', appName: 'HomeStar', system: 'iOS', region: '国内',
    configs: [
      { key: 'c1', adSource: 1, adPlatform: 'max', rate: 15.00, testStatus: 'ok' },
      { key: 'c2', adSource: 1, adPlatform: 'fengchuan', rate: 10.00, testStatus: 'fail' },
    ],
  },
  { appId: 'HS_AND_CN', appName: 'HomeStar', system: 'Android', region: '国内', configs: [] },
  { appId: 'PC_IOS_OS', appName: 'ParkingCloud', system: 'iOS', region: '海外', configs: [] },
  { appId: 'HM_AND_CN', appName: 'HM IoT App', system: 'Android', region: '国内', configs: [] },
];

const AdCommissionConfig = ({ isPlatform }) => {
  const [apps, setApps] = useState(MOCK_AD_APPS);
  const [expandedApp, setExpandedApp] = useState(null);
  const [editVisible, setEditVisible] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();

  const openAdd = (app) => {
    setEditingApp(app); setEditingConfig(null); form.resetFields();
    form.setFieldsValue({ adSource: 1, rate: 0 });
    setEditVisible(true);
  };
  const openEdit = (app, cfg) => {
    setEditingApp(app); setEditingConfig(cfg);
    form.setFieldsValue({ adSource: cfg.adSource, adPlatform: cfg.adPlatform, rate: cfg.rate ?? 0 });
    setEditVisible(true);
  };
  const removeConfig = (app, cfg) => {
    setApps((prev) => prev.map((a) => (a.appId === app.appId ? { ...a, configs: a.configs.filter((c) => c.key !== cfg.key) } : a)));
    message.success('已移除');
  };
  const saveConfig = async () => {
    try {
      const v = await form.validateFields();
      const duplicated = editingApp.configs.some((c) => c.adSource === v.adSource && c.adPlatform === v.adPlatform
        && !(editingConfig && c.key === editingConfig.key));
      if (duplicated) { message.error('该「广告源 + 广告平台」已配置'); return; }
      const rate = (v.rate == null || v.rate === '') ? null : Number(v.rate);
      setApps((prev) => prev.map((a) => {
        if (a.appId !== editingApp.appId) return a;
        if (editingConfig) {
          return { ...a, configs: a.configs.map((c) => (c.key === editingConfig.key ? { ...c, adSource: v.adSource, adPlatform: v.adPlatform, rate, testStatus: null } : c)) };
        }
        return { ...a, configs: [...a.configs, { key: `c_${Date.now()}`, adSource: v.adSource, adPlatform: v.adPlatform, rate, testStatus: null }] };
      }));
      message.success(editingConfig ? '已更新' : '已添加');
      setEditVisible(false);
    } catch (e) { /* 校验失败 */ }
  };
  const runTest = (app, cfg) => {
    // 原型演示：除 AdMob 外均视为已配置广告账号（可访问）
    const ok = cfg.adPlatform !== 'AdMob';
    setApps((prev) => prev.map((a) => (a.appId === app.appId ? { ...a, configs: a.configs.map((c) => (c.key === cfg.key ? { ...c, testStatus: ok ? 'ok' : 'fail' } : c)) } : a)));
    if (ok) message.success('获取账单正常');
    else message.error('获取广告账单失败，请联系开发工程师排查，是否已配置该广告账号。');
  };

  const appColumns = [
    {
      title: '应用（App-系统-区域）', key: 'app', width: 260,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.appName}-{r.system}-{r.region}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.appId}</Text>
        </div>
      ),
    },
    {
      title: '已配置平台', key: 'count', width: 140, align: 'center',
      render: (_, r) => (r.configs.length ? <Text>{r.configs.length} 个</Text> : <Text type="secondary">未配置</Text>),
    },
    {
      title: '操作', key: 'action', width: 140,
      render: (_, r) => (isPlatform
        ? <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => openAdd(r)}>添加配置</Button>
        : <Text type="secondary">只读</Text>),
    },
  ];

  const configColumns = [
    { title: '广告源', dataIndex: 'adSource', key: 'adSource', width: 120, render: (v) => <Tag color={AD_SOURCE_COLOR[v]}>{AD_SOURCE_LABEL[v] || '-'}</Tag> },
    {
      title: '广告平台', dataIndex: 'adPlatform', key: 'adPlatform', width: 160,
      render: (v) => (
        <Space size={4}>
          <Tag color="geekblue" style={{ border: 'none' }}>{v}</Tag>
          {platformStatusMap[v] !== 1 && <Tag color="default">平台已停用</Tag>}
        </Space>
      ),
    },
    {
      title: '抽佣比例', key: 'rate', width: 120, align: 'right',
      render: (_, r) => (r.rate == null
        ? <Text type="secondary">不抽佣</Text>
        : <Text style={{ color: '#409eff', fontWeight: 600 }}>{Number(r.rate).toFixed(2)}%</Text>),
    },
    {
      title: '获取广告账单', key: 'test', width: 300,
      render: (_, r) => (
        <Space size={8}>
          <Button size="small" icon={<ExperimentOutlined />} onClick={() => runTest(expandedApp, r)}>测试获取账单</Button>
          {r.testStatus === 'ok' && <Text style={{ color: '#52c41a' }}>获取账单正常</Text>}
          {r.testStatus === 'fail' && <Text type="danger" style={{ fontSize: 12 }}>获取广告账单失败，请联系开发工程师排查，是否已配置该广告账号。</Text>}
        </Space>
      ),
    },
  ];
  if (isPlatform) {
    configColumns.push({
      title: '操作', key: 'action', width: 130,
      render: (_, r) => (
        <Space size={0}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(expandedApp, r)}>编辑</Button>
          <Popconfirm title="确认移除该配置？" okText="移除" cancelText="取消" onConfirm={() => removeConfig(expandedApp, r)}>
            <Button type="link" size="small" danger>移除</Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 15 }}>App广告抽佣</Text>
        {!isPlatform && <Text type="secondary" style={{ fontSize: 12 }}>只读，由平台配置</Text>}
      </div>

      <Alert
        type="info" showIcon style={{ marginBottom: 8 }}
        message={<span style={{ fontWeight: 600 }}>企业广告源</span>}
        description="App 广告收入由企业自行在广告平台结算，资金不经过平台。平台通过已配置的广告账号拉取该 App 的广告收益账单，按月按抽佣比例出账；平台账务确认后，从企业《消费账户》扣除平台广告抽佣。"
      />
      <Alert
        type="success" showIcon style={{ marginBottom: 16 }}
        message={<span style={{ fontWeight: 600 }}>平台广告源</span>}
        description="App 广告收入直接结算至平台账户。平台通过已配置的广告账号拉取广告收益账单，按月按抽佣比例出账；平台账务确认后，扣除抽佣后的净额入企业《收益账户》，企业可发起提现。"
      />

      <Table
        rowKey="appId"
        columns={appColumns}
        dataSource={apps}
        pagination={false}
        size="small"
        bordered
        expandable={{
          expandedRowRender: (record) => (
            <Table
              rowKey="key"
              size="small"
              columns={configColumns}
              dataSource={record.configs || []}
              pagination={false}
              locale={{ emptyText: '暂无配置，点击「添加配置」为该 AppID 配置广告源与广告平台' }}
            />
          ),
          onExpand: (expanded, record) => setExpandedApp(expanded ? record : null),
        }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: 'inline-block', marginTop: 8 }}>
        <InfoCircleOutlined style={{ marginRight: 4 }} />每个 AppID 默认为空配置；抽佣比例留空表示不抽佣，同一 AppID 下多平台合计可超过 100%。
      </Text>

      <Modal
        title={`${editingConfig ? '编辑' : '添加'}广告抽佣配置 - ${editingApp?.appName || ''}`}
        open={editVisible}
        onOk={saveConfig}
        onCancel={() => setEditVisible(false)}
        okText="保存" cancelText="取消" destroyOnClose width={480}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="adSource" label="广告源" rules={[{ required: true, message: '请选择广告源' }]}>
            <Radio.Group>
              <Radio value={1}>企业广告源</Radio>
              <Radio value={2}>平台广告源</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="adPlatform" label="广告平台" rules={[{ required: true, message: '请选择广告平台' }]}
            extra="仅可选广告平台字典中启用的一级平台">
            <Select placeholder="请选择广告平台" options={ENABLED_PLATFORM_OPTIONS} />
          </Form.Item>
          <Form.Item name="rate" label="抽佣比例" extra="0~100%，保留 2 位小数；留空或 0 表示不抽佣">
            <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" placeholder="留空表示不抽佣" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

const SCENE_LABEL = { gift: '企业赠送套餐', self: 'App企业自收款', platform: 'App平台代收款' };

const SceneTitle = ({ title, hint }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
    <Text strong style={{ fontSize: 15 }}>{title}</Text>
    {hint && <Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text>}
  </div>
);

const BusinessConfig = ({ isPlatform = true, enterpriseName = '', section = 'all' }) => {

  const [licenseDiscount, setLicenseDiscount] = useState(MOCK_LICENSE_DISCOUNT);
  const [licenseVisible, setLicenseVisible] = useState(false);
  const [licenseForm] = Form.useForm();

  // 三块场景各自的底价配置（国内/海外默认 + 服务类型覆盖）
  const [giftConfig, setGiftConfig] = useState(MOCK_GIFT_CONFIG);
  const [selfConfig, setSelfConfig] = useState(MOCK_SELF_CONFIG);

  // 抽佣与手续费（场景级，区域无关）
  const [selfComm, setSelfComm] = useState(MOCK_SELF_COMMISSION);
  const [platformComm, setPlatformComm] = useState(MOCK_PLATFORM_COMMISSION);

  // 场景商务配置编辑（抽屉，同时编辑国内/海外）
  const [sceneDrawer, setSceneDrawer] = useState({ visible: false, scene: 'gift' });
  const [sceneForm] = Form.useForm();

  // 手续费草稿（随默认策略弹窗一起编辑）
  const [feeDraft, setFeeDraft] = useState({});
  const [feeModalVisible, setFeeModalVisible] = useState(false);

  const now = () => new Date().toLocaleString('zh-CN', { hour12: false });

  const sceneState = { gift: giftConfig, self: selfConfig };
  const sceneSetter = { gift: setGiftConfig, self: setSelfConfig };
  const commState = { self: selfComm, platform: platformComm };
  const commSetter = { self: setSelfComm, platform: setPlatformComm };

  // ═══════════ 场景商务配置（抽屉） ═══════════
  const openSceneEditor = (scene) => {
    const fields = {};
    if (scene !== 'platform') {
      const config = sceneState[scene];
      ['cn', 'os'].forEach((region) => {
        fields[region] = {
          isDeductBasePrice: config.default[region].isDeductBasePrice,
          discountRatio: config.default[region].discountRatio,
          remark: config.default[region].remark || '',
        };
      });
    }
    if (scene !== 'gift') {
      ['cn', 'os'].forEach((region) => {
        const comm = commState[scene][region] || {};
        fields[region] = fields[region] || {};
        fields[region].isCommission = comm.isCommission;
        fields[region].commissionRate = comm.commissionRate;
        fields[region].isCommissionDeductBasePrice = comm.isCommissionDeductBasePrice
          ?? (scene === 'platform' ? true : sceneState[scene].default[region].isDeductBasePrice);
        if (scene === 'platform') fields[region].commissionRemark = comm.remark || '';
      });
      fields.isDeductPaymentFee = commState[scene].isDeductPaymentFee;
      setFeeDraft({ ...(commState[scene].paymentFees || {}) });
    }
    sceneForm.setFieldsValue(fields);
    setSceneDrawer({ visible: true, scene });
  };

  const saveSceneEditor = async () => {
    try {
      const v = await sceneForm.validateFields();
      const { scene } = sceneDrawer;
      const regionDefault = (region) => ({
        isDeductBasePrice: !!v[region]?.isDeductBasePrice,
        discountRatio: v[region]?.isDeductBasePrice ? Number(v[region].discountRatio) : 100,
        remark: v[region]?.remark || '',
      });
      if (scene !== 'platform') {
        sceneSetter[scene]((prev) => ({
          ...prev,
          default: { cn: regionDefault('cn'), os: regionDefault('os') },
          updatedAt: now(), operator: 'admin',
        }));
      }
      if (scene !== 'gift') {
        commSetter[scene]((prev) => {
          const build = (region) => ({
            isCommission: !!v[region]?.isCommission,
            commissionRate: v[region]?.commissionRate == null ? prev[region].commissionRate : Number(v[region].commissionRate),
            isCommissionDeductBasePrice: !!v[region]?.isCommissionDeductBasePrice,
            ...(scene === 'platform' ? { remark: v[region]?.commissionRemark || '' } : {}),
          });
          return {
            ...prev,
            cn: build('cn'),
            os: build('os'),
            isDeductPaymentFee: !!v.isDeductPaymentFee,
            paymentFees: feeDraft,
            updatedAt: now(), operator: 'admin',
          };
        });
      }
      message.success('商务配置已更新');
      setSceneDrawer((prev) => ({ ...prev, visible: false }));
    } catch (e) { /* 校验失败 */ }
  };

  const applySceneService = (scene, serviceId, value) => {
    sceneSetter[scene]((prev) => {
      const services = { ...prev.services };
      if (value) services[serviceId] = value; else delete services[serviceId];
      return { ...prev, services, updatedAt: now(), operator: 'admin' };
    });
    message.success(value ? '服务类型策略已添加' : '已删除，该服务类型按本场景默认策略生效');
  };

  // ═══════════ 策略行（国内 / 海外各一行） ═══════════
  const renderPolicyRow = (scene, region) => {
    const policy = sceneState[scene].default[region];
    const comm = scene !== 'gift' ? commState[scene][region] : null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: '10px 14px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8 }}>
        <Space size={6}>
          <Tag style={{ border: 'none', margin: 0 }}>{REGION_LABEL[region]}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{region === 'cn' ? 'CNY' : 'USD'}</Text>
        </Space>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>扣除底价</Text>
          <Badge status={policy.isDeductBasePrice ? 'success' : 'default'} />
          <Text style={{ color: policy.isDeductBasePrice ? '#52c41a' : '#999' }}>
            {policy.isDeductBasePrice ? '扣除' : '不扣除'}
          </Text>
          {policy.isDeductBasePrice && (
            <Text strong style={{ color: '#1677ff' }}>{formatDiscount(policy.discountRatio)}</Text>
          )}
        </span>
        {comm && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>抽佣</Text>
            {comm.isCommission ? (
              <>
                <Text strong style={{ color: '#f50' }}>抽佣 {Number(comm.commissionRate).toFixed(2)}%</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  抽佣扣底价：{comm.isCommissionDeductBasePrice ? '扣除' : '不扣除'}
                </Text>
              </>
            ) : <Text type="secondary">不抽佣</Text>}
          </span>
        )}
        {policy.remark && <Text type="secondary" style={{ fontSize: 12 }}>备注：{policy.remark}</Text>}
      </div>
    );
  };

  const renderPlatformCommissionRow = (region) => {
    const comm = commState.platform[region] || {};
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: '10px 14px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8 }}>
        <Space size={6}>
          <Tag style={{ border: 'none', margin: 0 }}>{REGION_LABEL[region]}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{region === 'cn' ? 'CNY' : 'USD'}</Text>
        </Space>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>抽佣</Text>
          {comm.isCommission ? (
            <>
              <Text strong style={{ color: '#f50' }}>抽佣 {Number(comm.commissionRate).toFixed(2)}%</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                抽佣时扣底价：{comm.isCommissionDeductBasePrice ? '扣除' : '不扣除'}
              </Text>
            </>
          ) : <Text type="secondary">不抽佣</Text>}
        </span>
        {comm.remark && <Text type="secondary" style={{ fontSize: 12 }}>备注：{comm.remark}</Text>}
      </div>
    );
  };

  const renderFeeRow = (scene) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#fafafa', border: '1px dashed #e8e8e8', borderRadius: 8 }}>
      <Text type="secondary" style={{ fontSize: 13 }}>手续费</Text>
      <Badge status={commState[scene].isDeductPaymentFee ? 'success' : 'default'} />
      <Text style={{ fontSize: 13 }}>
        {commState[scene].isDeductPaymentFee
          ? `扣除 · ${countConfiguredFees(commState[scene].paymentFees)} 个支付方式已配置`
          : '不扣除'}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>不区分国内 / 海外</Text>
    </div>
  );

  const showBase = section === 'all' || section === 'base';
  const showPrice = section === 'all' || section === 'price';

  return (
    <div>
      {showBase && (
        <>
          {/* License 折扣 */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', border: '1px solid #f0f0f0', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 15 }}>License 折扣</Text>
              {isPlatform && <Button type="link" icon={<EditOutlined />} onClick={() => { licenseForm.setFieldsValue({ licenseDiscount }); setLicenseVisible(true); }}>设置</Button>}
            </div>
            <div><Text type="secondary">折扣比例：</Text><Text strong style={{ fontSize: 18, color: '#1677ff', marginLeft: 8 }}>{licenseDiscount}%</Text></div>
          </div>

          {/* 设备流量商务（只读，实际编辑在《设备流量商务》页面维护） */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', border: '1px solid #f0f0f0', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 15 }}>设备流量商务</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>在《设备流量商务》页面维护</Text>
            </div>
            {MOCK_TRAFFIC_GROUP ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 48px' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>收费规则</Text>
                  <div style={{ marginTop: 4 }}><Tag color="blue" style={{ border: 'none' }}>{MOCK_TRAFFIC_GROUP.name}</Tag></div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>国内单价</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>¥ {MOCK_TRAFFIC_GROUP.cnyPrice.toFixed(2)}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}> /G</Text>
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>海外默认单价</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>$ {MOCK_TRAFFIC_GROUP.usdPrice.toFixed(2)}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}> /G</Text>
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>汇率（USD→CNY）</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>{MOCK_TRAFFIC_GROUP.exchangeRate.toFixed(2)}</Text>
                  </div>
                </div>
                {MOCK_TRAFFIC_GROUP.countryPrices?.length > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 13 }}>国家精细价</Text>
                    <div style={{ marginTop: 4 }}>
                      <Space size={4} wrap>
                        {MOCK_TRAFFIC_GROUP.countryPrices.map(c => (
                          <Tag key={c.country} color="geekblue" style={{ border: 'none' }}>{c.country} $ {c.usdPrice.toFixed(2)}/G</Tag>
                        ))}
                      </Space>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text type="secondary">收费规则：</Text>
                <Tag style={{ marginLeft: 8, border: 'none' }}>未关联</Tag>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>未关联收费组的企业不产生流量扣费</Text>
              </div>
            )}
          </div>
        </>
      )}

      {showPrice && (
        <>
          {/* 一、企业赠送套餐 */}
          <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
            <SceneTitle title="企业赠送套餐 — 扣除底价配置" hint="无 App 级配置，按赠送目标所属区域与服务类型生效" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Text strong style={{ fontSize: 14 }}>默认赠送策略</Text>
              {isPlatform && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openSceneEditor('gift')}>编辑</Button>}
            </div>
            {renderPolicyRow('gift', 'cn')}
            {renderPolicyRow('gift', 'os')}
            <div style={{ marginTop: 20 }}>
              <ServiceTypePolicyTable
                serviceTypes={MOCK_SERVICE_TYPES}
                overrides={giftConfig.services}
                fallback={giftConfig.default}
                isPlatform={isPlatform}
                title="自定义服务"
                layerLabel="本场景默认策略"
                onChange={(serviceId, value) => applySceneService('gift', serviceId, value)}
              />
            </div>
            {giftConfig.updatedAt && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb' }}>
                最近更新 {giftConfig.updatedAt} · 操作人：{giftConfig.operator}
              </div>
            )}
            <Alert
              type="info" showIcon style={{ marginTop: 12 }}
              message="赠送套餐按赠送目标所属区域生效；目标区域无法判定时按国内默认策略处理。"
            />
          </div>

          {/* 二、App企业自收款 */}
          <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
            <SceneTitle title="App企业自收款 — 商务配置" hint="底价在订单产生时从企业《消费账户》实时扣除" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Text strong style={{ fontSize: 14 }}>默认底价与抽佣</Text>
              {isPlatform && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openSceneEditor('self')}>编辑</Button>}
            </div>
            {renderPolicyRow('self', 'cn')}
            {renderPolicyRow('self', 'os')}
            <div style={{ marginTop: 8 }}>{renderFeeRow('self')}</div>
            <div style={{ marginTop: 20 }}>
              <ServiceTypePolicyTable
                serviceTypes={MOCK_SERVICE_TYPES}
                overrides={selfConfig.services}
                fallback={selfConfig.default}
                isPlatform={isPlatform}
                title="自定义服务"
                layerLabel="本场景默认策略"
                onChange={(serviceId, value) => applySceneService('self', serviceId, value)}
              />
            </div>
            {selfConfig.updatedAt && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb' }}>
                最近更新 {selfConfig.updatedAt} · 操作人：{selfConfig.operator}
              </div>
            )}
          </div>

          {/* 三、App平台代收款 */}
          <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px' }}>
            <SceneTitle title="App平台代收款 — 抽佣配置" hint="只配置抽佣；底价不实时扣除，抽佣出账时按「抽佣时扣底价」参与计算" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Text strong style={{ fontSize: 14 }}>抽佣</Text>
              {isPlatform && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openSceneEditor('platform')}>编辑</Button>}
            </div>
            {renderPlatformCommissionRow('cn')}
            {renderPlatformCommissionRow('os')}
            <div style={{ marginTop: 8 }}>{renderFeeRow('platform')}</div>
            {platformComm.updatedAt && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb' }}>
                最近更新 {platformComm.updatedAt} · 操作人：{platformComm.operator}
              </div>
            )}
          </div>
        </>
      )}

      {/* License折扣弹窗 */}
      <Modal title={`设置License折扣 - ${enterpriseName}`} open={licenseVisible} onOk={async () => { try { const v = await licenseForm.validateFields(); setLicenseDiscount(v.licenseDiscount); message.success('License折扣已更新'); setLicenseVisible(false); } catch (e) { /* 校验失败 */ } }} onCancel={() => setLicenseVisible(false)} okText="确定" cancelText="取消" destroyOnClose width={420}>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">当前折扣为 <Text strong>{licenseDiscount}%</Text>，调整后立即生效。</Text>
          <Form form={licenseForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="licenseDiscount" label="License采购折扣" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} precision={0} style={{ width: '100%' }} addonAfter="%" />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* 商务配置抽屉：同时编辑国内与海外 */}
      <Drawer
        title={`编辑商务配置 - ${SCENE_LABEL[sceneDrawer.scene]}`}
        open={sceneDrawer.visible}
        onClose={() => setSceneDrawer((prev) => ({ ...prev, visible: false }))}
        width="min(960px, 100vw)"
        destroyOnClose
        footer={(
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSceneDrawer((prev) => ({ ...prev, visible: false }))}>取消</Button>
              <Button type="primary" onClick={saveSceneEditor}>保存</Button>
            </Space>
          </div>
        )}
      >
        <Form form={sceneForm} layout="vertical">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
          {['cn', 'os'].map((region) => (
            <div key={region} style={{ flex: '1 1 0', minWidth: 0, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {REGION_LABEL[region]}（{region === 'cn' ? 'CNY' : 'USD'}）
              </div>
              {sceneDrawer.scene !== 'platform' && (
                <>
                  <Form.Item name={[region, 'isDeductBasePrice']} label="是否扣除套餐底价" valuePropName="checked" style={{ marginBottom: 8 }}>
                    <Switch checkedChildren="扣除" unCheckedChildren="不扣" />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(p, c) => p?.[region]?.isDeductBasePrice !== c?.[region]?.isDeductBasePrice}>
                    {({ getFieldValue }) => (getFieldValue([region, 'isDeductBasePrice']) ? (
                      <Form.Item
                        name={[region, 'discountRatio']} label="扣除折扣比例" style={{ marginBottom: 8 }}
                        rules={[{ required: true, message: '请输入扣除折扣比例' }]}
                        extra="实际扣减额 = 对应区域套餐底价 × 折扣比例；0% 等同不扣除，100% 为不打折"
                      >
                        <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                      </Form.Item>
                    ) : null)}
                  </Form.Item>
                  <Form.Item name={[region, 'remark']} label="备注" style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={2} maxLength={200} showCount placeholder="记录商务背景" />
                  </Form.Item>
                </>
              )}

              {sceneDrawer.scene !== 'gift' && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8e8' }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>抽佣（{REGION_LABEL[region]}）</div>
                  <Form.Item name={[region, 'isCommission']} label="是否抽佣" valuePropName="checked" style={{ marginBottom: 8 }}>
                    <Switch checkedChildren="抽佣" unCheckedChildren="不抽佣" />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(p, c) => p?.[region]?.isCommission !== c?.[region]?.isCommission}>
                    {({ getFieldValue }) => (getFieldValue([region, 'isCommission']) ? (
                      <>
                        <Form.Item name={[region, 'commissionRate']} label="抽佣比例" rules={[{ required: true, message: '请输入抽佣比例' }]} style={{ marginBottom: 8 }}>
                          <InputNumber min={0.01} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                        </Form.Item>
                        <Form.Item
                          name={[region, 'isCommissionDeductBasePrice']}
                          label="抽佣时扣除套餐底价"
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                          extra={sceneDrawer.scene === 'platform'
                            ? '代收款底价不实时扣除，在抽佣时按此开关决定底价是否参与计算；关闭则仅按订单实付与手续费计算'
                            : '开启：抽佣基数 = 订单实付金额 − 底价扣减额 − 支付手续费（如扣除）；关闭：底价不参与抽佣计算。默认跟随本区域底价策略'}
                        >
                          <Switch checkedChildren="扣除" unCheckedChildren="不扣" />
                        </Form.Item>
                        {sceneDrawer.scene === 'platform' && (
                          <Form.Item name={[region, 'commissionRemark']} label="备注" style={{ marginTop: 12, marginBottom: 0 }} extra="该备注用于抽佣">
                            <Input.TextArea rows={2} maxLength={200} showCount placeholder="记录抽佣商务背景" />
                          </Form.Item>
                        )}
                      </>
                    ) : null)}
                  </Form.Item>
                </div>
              )}
            </div>
          ))}
          </div>

          {sceneDrawer.scene !== 'gift' && (
            <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>手续费（不区分国内 / 海外）</div>
              <Form.Item name="isDeductPaymentFee" label="是否扣除" valuePropName="checked" style={{ marginBottom: 8 }}>
                <Switch checkedChildren="扣除" unCheckedChildren="不扣" />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(p, c) => p.isDeductPaymentFee !== c.isDeductPaymentFee}>
                {({ getFieldValue: getVal }) => (getVal('isDeductPaymentFee') ? (
                  <Form.Item label="支付方式手续费" style={{ marginBottom: 0 }} extra="手续费仅扣减抽佣基数，不单独扣款；未配置的支付方式按 0 参与抽佣计算">
                    <Space size={8} wrap>
                      <Text type="secondary" style={{ fontSize: 12 }}>{countConfiguredFees(feeDraft)}/{PAYMENT_CHANNELS.length} 个支付方式已配置</Text>
                      <Button size="small" icon={<SettingOutlined />} onClick={() => setFeeModalVisible(true)}>设置费率</Button>
                    </Space>
                  </Form.Item>
                ) : null)}
              </Form.Item>
            </div>
          )}
        </Form>
      </Drawer>

      {/* 支付方式手续费 */}
      <PaymentFeeModal
        open={feeModalVisible}
        value={feeDraft}
        pullable={sceneDrawer.scene === 'platform'}
        onChange={setFeeDraft}
        onClose={() => setFeeModalVisible(false)}
      />

    </div>
  );
};

// ══════════════════════════════════════════════
//  私有化部署商务（企业详情-独立 Tab，仅平台可见可编辑）
// ══════════════════════════════════════════════
const PrivateDeployBizSection = ({ isPlatform = true, enterpriseName = '' }) => {
  const [privateDeployBiz, setPrivateDeployBiz] = useState(MOCK_PRIVATE_DEPLOY_BIZ);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const now = () => new Date().toLocaleString('zh-CN', { hour12: false });

  return (
    <div>
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', border: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 15 }}>私有化部署商务</Text>
          {isPlatform && <Button type="link" icon={<EditOutlined />} onClick={() => { form.setFieldsValue(privateDeployBiz); setVisible(true); }}>设置</Button>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 48px' }}>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>月租费成本比</Text>
            <div style={{ marginTop: 4 }}>
              {privateDeployBiz.monthlyRentRatio != null
                ? <Text strong style={{ fontSize: 18, color: '#1677ff' }}>{privateDeployBiz.monthlyRentRatio} 倍</Text>
                : <Text type="secondary">未配置</Text>}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>弹性云资源用量成本比</Text>
            <div style={{ marginTop: 4 }}>
              {privateDeployBiz.elasticUsageRatio != null
                ? <Text strong style={{ fontSize: 18, color: '#1677ff' }}>{privateDeployBiz.elasticUsageRatio} 倍</Text>
                : <Text type="secondary">未配置</Text>}
            </div>
          </div>
        </div>
        {privateDeployBiz.updatedAt && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#bbb' }}>最近更新 {privateDeployBiz.updatedAt} · 操作人：{privateDeployBiz.operator}</div>
        )}
      </div>

      <Modal title={`设置私有化部署商务 - ${enterpriseName}`} open={visible}
        onOk={async () => {
          try {
            const v = await form.validateFields();
            const normalize = (x) => (x == null || x === 0 ? null : x);
            setPrivateDeployBiz({
              monthlyRentRatio: normalize(v.monthlyRentRatio),
              elasticUsageRatio: normalize(v.elasticUsageRatio),
              updatedAt: now(), operator: 'admin',
            });
            message.success('私有化部署商务已更新'); setVisible(false);
          } catch (e) { /* 校验失败 */ }
        }}
        onCancel={() => setVisible(false)} okText="保存" cancelText="取消" destroyOnClose width={480}>
        <div style={{ marginTop: 16 }}>
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="未配置（留空或 0）时，该企业的私有化部署审核与升配将无法通过。此配置仅平台可见，企业侧无感知。" />
          <Form form={form} layout="vertical">
            <Form.Item name="monthlyRentRatio" label="月租费成本比（倍数）"
              extra="企业需付/月 = 平台成本/月 × 此倍数，如平台成本 1000、填 1.5 则企业需付 1500">
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} addonAfter="倍" placeholder="如 1.5" />
            </Form.Item>
            <Form.Item name="elasticUsageRatio" label="弹性云资源用量成本比（倍数）"
              extra="企业需付单价 = 成本单价 × 此倍数">
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} addonAfter="倍" placeholder="如 1.2" />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export { AdCommissionConfig, PrivateDeployBizSection };
export default BusinessConfig;
